import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../lib/format';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
// Removed import - using local formatCurrency function instead
import { useNotifications } from '../hooks/useNotifications';
import { InvoiceBreakdown } from '../components/InvoiceBreakdown';
import { PhysicianPayoutBreakdown } from '../components/PhysicianPayoutBreakdown';
import {
  DollarSign,
  Users,
  Search,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  Filter,
  Download,
  Send,
  UserPlus,
  RefreshCw,
  XCircle,
  HelpCircle
} from 'lucide-react';
import { formatDateTime, formatShortDate } from '../utils/dateUtils';

interface Payout {
  _id: string;
  recipientId: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
    distributorData?: {
      companyName?: string;
      paymentInstructions?: string;
    };
    supervisorData?: {
      companyName?: string;
      paymentInstructions?: string;
    };
    role?: string;
    email: string;
  };
  createdById: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
    email: string;
  };
  startDate: string;
  endDate: string;
  amount: number;
  status: 'pending' | 'sent' | 'confirmed' | 'cancelled' | 'failed';
  wireNumber?: string;
  description?: string;
  notesForRecipient?: string;
  sentAt?: string;
  confirmedAt?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Present only when this payout went out over a payment rail. Absence means it
   * was wired by hand.
   */
  rail?: {
    provider: string;
    clientPaymentId: string;
    paymentToken?: string;
    receiptToken?: string;
    /** PayQuicker's own status, stored verbatim — may be a value we don't know. */
    providerStatus?: string;
    settledAt?: string;
    lastError?: { code?: string; message?: string };
  };
  /** Derived server-side, because which subdocument holds the identity is role-dependent. */
  recipientPayQuickerStatus?: 'none' | 'invited' | 'in_progress' | 'approved' | 'rejected';
  recipientPayQuickerReady?: boolean;
  // Flattened by getPayoutsForAdmin for display.
  recipientName?: string;
  recipientEmail?: string;
  recipientCompany?: string | null;
}

interface OverlappingPayout {
  payoutId: string;
  startDate: string;
  endDate: string;
  amount: number;
  status: string;
  createdAt: string;
}

interface RevenuePreview {
  recipientId: string;
  startDate: string;
  endDate: string;
  // New revenue (not yet paid out)
  revenue: number;
  orderCount: number;
  invoiceCount: number;
  invoiceDetails?: Array<{
    invoiceId: string;
    invoiceNumber: string;
    totalAmount: number;
    paidAt: Date;
    physicianName: string;
    // Upline above the physician, frozen from the invoice's attribution.
    // Blank where a physician genuinely has no AM.
    accountManagerName?: string;
    supervisorName?: string;
    items: Array<{
      description: string;
      quantity: number;
      productCost: number;  // Floor/base price
      amCost: number;       // AM's cost (catalog price)
      amPrice: number;      // AM's sell price (invoice price)
      unitMargin: number;   // Per-unit profit
      amPayout: number;     // Total profit
    }>;
    totalAmPayout: number;
  }>;
  // Previously paid out information
  previouslyPaidRevenue: number;
  previouslyPaidOrderCount: number;
  previouslyPaidInvoiceCount: number;
  // Total revenue in date range (new + previously paid)
  totalRevenueInRange: number;
  totalOrdersInRange: number;
  totalInvoicesInRange: number;
  // Overlapping payouts warning
  overlappingPayouts: OverlappingPayout[];
  hasOverlappingPayouts: boolean;
}

interface User {
  _id: string;
  profile: {
    firstName: string;
    lastName: string;
  };
  distributorData?: {
    companyName?: string;
    paymentInstructions?: string;
  };
  supervisorData?: {
    companyName?: string;
    paymentInstructions?: string;
  };
  email: string;
  role: string;
}

// Helper to get company name from either distributor or supervisor data
function getCompanyName(obj: any): string {
  return obj?.supervisorData?.companyName || obj?.distributorData?.companyName
    || obj?.physicianData?.clinicName || obj?.profile?.clinicName || '';
}

// Local utility function

// Now we get flattened user data from the backend, so these are simple accessors

export const AdminPayouts: React.FC = () => {
  const user = useUser();
  const { notifySuccess, notifyError } = useNotifications();
  
  // State management
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [distributors, setDistributors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [payoutInvoiceBreakdown, setPayoutInvoiceBreakdown] = useState<any>(null);
  const [loadingInvoiceBreakdown, setLoadingInvoiceBreakdown] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [sendingRailPayoutId, setSendingRailPayoutId] = useState<string | null>(null);
  const [invitingRecipientId, setInvitingRecipientId] = useState<string | null>(null);
  const [reconciling, setReconciling] = useState(false);
  
  // Create payout form state
  const [createForm, setCreateForm] = useState({
    recipientId: '',
    startDate: '',
    endDate: '',
    amount: '',
    wireNumber: '',
    description: '',
    notesForRecipient: ''
  });
  const [revenuePreview, setRevenuePreview] = useState<RevenuePreview | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bulk drafts state
  const defaultBulkRange = (() => {
    const today = new Date();
    const day = today.getDate();
    const y = today.getFullYear();
    const m = today.getMonth();
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    if (day <= 15) {
      // Mid-month: previous month's 16th -> end
      const start = new Date(y, m - 1, 16);
      const end = new Date(y, m, 0); // last day of prev month
      return { startDate: fmt(start), endDate: fmt(end) };
    }
    // End of month: this month's 1st -> 15th
    return { startDate: fmt(new Date(y, m, 1)), endDate: fmt(new Date(y, m, 15)) };
  })();

  const [bulkRange, setBulkRange] = useState(defaultBulkRange);
  const [bulkRows, setBulkRows] = useState<Array<{
    recipientId: string;
    recipientName: string;
    recipientEmail: string;
    recipientCompany: string | null;
    recipientRole: 'distributor' | 'supervisor' | 'physician' | 'partner';
    // Who this recipient rolls up to. Empty where there is nobody above:
    // an AM has no AM, a supervisor has neither.
    accountManagerName?: string;
    supervisorName?: string;
    amount: number;
    orderCount: number;
    invoiceCount: number;
    membershipEarnings: number;
    overrideEarnings?: number;
    hasOverlappingPayout: boolean;
    paymentInstructions?: string;
  }>>([]);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkErrors, setBulkErrors] = useState<Array<{ recipientId: string; error: string }>>([]);
  
  // Distributor search/selection state
  const [distributorSearch, setDistributorSearch] = useState('');
  const recipientsSeq = useRef(0);
  // Total matching clinics when only the first page of them was loaded (-1: unknown).
  const [moreClinicsTotal, setMoreClinicsTotal] = useState<number | null>(null);
  const [showDistributorDropdown, setShowDistributorDropdown] = useState(false);
  const [distributorPage, setDistributorPage] = useState(1);
  const DISTRIBUTORS_PER_PAGE = 7;
  // The chosen recipient's full record, captured at selection time so the
  // "selected" summary survives the recipient list being replaced by a later
  // search (the selected user may not be in the current server results).
  const [selectedRecipient, setSelectedRecipient] = useState<any>(null);

  // Load data on component mount
  useEffect(() => {
    if (user?.role === 'admin') {
      loadPayouts();
    }
  }, [user]);

  // Recipient loading is search-driven and server-side: debounce the search box
  // and re-query the backend so recipients past the first page are reachable
  // (previously the dropdown loaded once and filtered in-browser, hiding anyone
  // beyond the fetch cap). An empty term loads the default recipient list.
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const handle = setTimeout(() => {
      loadDistributors(distributorSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [distributorSearch, user]);

  // Close distributor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.distributor-dropdown-container')) {
        setShowDistributorDropdown(false);
      }
    };

    if (showDistributorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDistributorDropdown]);

  const loadPayouts = async () => {
    try {
      console.log('Loading payouts...');
      setLoading(true);
      const response = await apiClient.getPayouts();
      console.log('Payouts response:', response);
      
      if (response.success && response.data) {
        setPayouts(response.data);
      } else {
        console.error('Failed to load payouts:', response.error);
        setError(response.error || 'Failed to load payouts');
      }
    } catch (error: any) {
      console.error('Error loading payouts:', error);
      setError('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const loadDistributors = async (search?: string) => {
    // A slower answer to an earlier search must not overwrite a newer one.
    const mine = ++recipientsSeq.current;
    try {
      const term = (search ?? '').trim();
      // Fetch distributors, supervisors AND physicians as payout recipients,
      // each searched server-side so recipients beyond one page are reachable.
      // Supervisors and AMs are walked in full; clinics far outnumber one page,
      // so show the first matches and say so.
      const [amsList, supervisorsList, physResponse] = await Promise.all([
        apiClient.getAllUsers({ role: 'distributor', search: term || undefined }),
        apiClient.getAllUsers({ role: 'supervisor', search: term || undefined }),
        apiClient.searchUsersPage({ role: 'physician', search: term, pageSize: 100 })
      ]);
      if (mine !== recipientsSeq.current) return;
      setDistributors([...supervisorsList, ...amsList, ...physResponse.items]);
      setMoreClinicsTotal(physResponse.hasMore ? (physResponse.total ?? -1) : null);
    } catch (error: any) {
      if (mine === recipientsSeq.current) console.error('Error loading recipients:', error);
    }
  };

  const calculateRevenuePreview = async () => {
    if (!createForm.recipientId || !createForm.startDate || !createForm.endDate) {
      return;
    }

    try {
      console.log('Calculating revenue preview with params:', {
        recipientId: createForm.recipientId,
        startDate: createForm.startDate,
        endDate: createForm.endDate
      });
      
      const response = await apiClient.getRevenuePreview({
        recipientId: createForm.recipientId,
        startDate: createForm.startDate,
        endDate: createForm.endDate
      });
      
      console.log('Revenue preview response:', response);
      
      if (response.success) {
        setRevenuePreview(response.data);
        setCreateForm(prev => ({ ...prev, amount: response.data.revenue.toString() }));
      } else {
        console.error('Revenue preview error:', response.error);
        notifyError('Revenue Calculation Failed', response.error || 'Failed to calculate revenue');
      }
    } catch (error: any) {
      console.error('Error calculating revenue preview:', error);
      notifyError('Revenue Calculation Failed', 'An error occurred while calculating revenue');
    }
  };

  const createPayout = async () => {
    try {
      setSubmitting(true);
      const response = await apiClient.createPayout({
        recipientId: createForm.recipientId,
        startDate: createForm.startDate,
        endDate: createForm.endDate,
        amount: parseFloat(createForm.amount),
        wireNumber: createForm.wireNumber || undefined,
        description: createForm.description || undefined,
        notesForRecipient: createForm.notesForRecipient || undefined
      });

      if (response.success) {
        await loadPayouts();
        setShowCreateModal(false);
        resetCreateForm();
        notifySuccess('Payout Created', 'The payout has been created successfully.');
      } else {
        const errorMsg = response.error || 'Failed to create payout';
        setError(errorMsg);
        notifyError('Failed to Create Payout', errorMsg);
      }
    } catch (error: any) {
      setError('Failed to create payout');
      console.error('Error creating payout:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const markPayoutAsSent = async (payoutId: string) => {
    try {
      const response = await apiClient.markPayoutAsSent(payoutId);
      if (response.success) {
        await loadPayouts();
        notifySuccess('Payout Marked as Sent', 'The payout has been marked as sent. The distributor can now confirm receipt.');
      } else {
        const errorMsg = response.error || 'Failed to mark payout as sent';
        setError(errorMsg);
        notifyError('Failed to Mark as Sent', errorMsg);
      }
    } catch (error: any) {
      setError('Failed to mark payout as sent');
      console.error('Error marking payout as sent:', error);
    }
  };

  /**
   * Send a payout over the PayQuicker rail.
   *
   * The outcomes are deliberately NOT collapsed into success/failure. Two of them
   * must never invite another attempt:
   *  - needs_reconciliation (202): a payment may already exist. Re-sending risks
   *    paying twice, so the sweep resolves it, not the admin.
   *  - failed (402): the instruction is dead AND the earnings have been released,
   *    so this payout is finished; a fresh payout is the way to pay them.
   */
  const sendViaPayQuicker = async (payoutId: string) => {
    setSendingRailPayoutId(payoutId);
    try {
      const response = await apiClient.sendPayoutViaPayQuicker(payoutId);
      await loadPayouts();

      if (response.success) {
        if (response.outcome === 'already_sent') {
          notifySuccess(
            'Payment Already Existed',
            'PayQuicker already had this payment, so it was adopted rather than sent again. No duplicate was created.'
          );
        } else {
          notifySuccess(
            'Sent to PayQuicker',
            'PayQuicker accepted the payment. Funds are not settled yet — the payout will confirm itself once PayQuicker reports the money landed.'
          );
        }
        return;
      }

      const message = response.error || 'Failed to send payout';

      switch (response.outcome) {
        case 'needs_reconciliation':
          setError(message);
          notifyError(
            'Outcome Unconfirmed — Do Not Re-send',
            `${message} A payment may already have gone out, so this payout is locked until reconciliation confirms what happened. It runs automatically every 15 minutes.`
          );
          break;
        case 'failed':
          setError(message);
          notifyError(
            'Payment Failed — Earnings Released',
            `${message} The earnings are back in the pool and will appear in the next payout run. Create a new payout rather than retrying this one.`
          );
          break;
        case 'rejected':
          setError(message);
          notifyError(
            'PayQuicker Rejected the Payment',
            `${message} Nothing was created, so this payout can be sent again once the cause is fixed.`
          );
          break;
        case 'ineligible':
          notifyError('Cannot Send via PayQuicker', message);
          break;
        default:
          setError(message);
          notifyError('Failed to Send Payout', message);
      }
    } catch (error: any) {
      setError('Failed to send payout via PayQuicker');
      console.error('Error sending payout via PayQuicker:', error);
    } finally {
      setSendingRailPayoutId(null);
    }
  };

  /** Invite a recipient to PayQuicker; they complete KYC on PayQuicker's side. */
  const inviteToPayQuicker = async (userId: string, recipientName: string) => {
    setInvitingRecipientId(userId);
    try {
      const response = await apiClient.invitePayQuickerRecipient(userId);
      await loadPayouts();

      if (response.success) {
        if (response.outcome === 'already_approved') {
          notifySuccess('Already Set Up', `${recipientName} is already approved on PayQuicker.`);
        } else {
          notifySuccess(
            'Invitation Sent',
            `PayQuicker emailed ${recipientName} to finish registration. They can be paid over the rail once approved.`
          );
        }
        return;
      }

      if (response.outcome === 'unknown') {
        notifyError(
          'Invitation Status Unconfirmed',
          `${response.error} An invitation may already have been sent — check PayQuicker before inviting again, to avoid emailing them twice.`
        );
        return;
      }

      notifyError('Failed to Invite', response.error || 'Failed to invite recipient');
    } catch (error: any) {
      console.error('Error inviting recipient to PayQuicker:', error);
      notifyError('Failed to Invite', 'Failed to invite recipient to PayQuicker');
    } finally {
      setInvitingRecipientId(null);
    }
  };

  /** On-demand reconciliation. Also runs automatically every 15 minutes. */
  const runRailReconciliation = async () => {
    setReconciling(true);
    try {
      const response = await apiClient.reconcileRailPayouts();
      if (response.success) {
        const s = response.data || {};
        await loadPayouts();
        notifySuccess(
          'Reconciliation Complete',
          `Examined ${s.examined ?? 0}: ${s.settled ?? 0} settled, ${s.failed ?? 0} failed, ${s.recovered ?? 0} recovered, ${s.releasedForResend ?? 0} released for re-send, ${s.unresolved ?? 0} still unresolved.`
        );
      } else {
        notifyError('Reconciliation Failed', response.error || 'Failed to reconcile payouts');
      }
    } catch (error: any) {
      console.error('Error reconciling payouts:', error);
      notifyError('Reconciliation Failed', 'Failed to reconcile payouts');
    } finally {
      setReconciling(false);
    }
  };

  const loadPayoutInvoiceBreakdown = async (payoutId: string) => {
    setLoadingInvoiceBreakdown(true);
    setPayoutInvoiceBreakdown(null);

    try {
      const response = await apiClient.getPayoutInvoiceBreakdown(payoutId);
      if (response.success) {
        setPayoutInvoiceBreakdown(response.data);
      } else {
        console.error('Failed to load invoice breakdown:', response.error);
      }
    } catch (error: any) {
      console.error('Error loading invoice breakdown:', error);
    } finally {
      setLoadingInvoiceBreakdown(false);
    }
  };

  const downloadPayoutCSV = async (payoutId: string) => {
    setDownloadingCSV(true);
    try {
      await apiClient.downloadPayoutCSV(payoutId);
      notifySuccess('CSV Downloaded', 'The payout data has been exported to CSV.');
    } catch (error: any) {
      console.error('Error downloading payout CSV:', error);
      notifyError('Download Failed', 'Failed to download the payout CSV. Please try again.');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const downloadPreviewCSV = () => {
    if (!revenuePreview || !revenuePreview.invoiceDetails) {
      notifyError('No Data', 'Please calculate revenue preview first.');
      return;
    }

    const selectedDistributor = getSelectedDistributor();
    const recipientName = selectedDistributor
      ? `${selectedDistributor.profile.firstName} ${selectedDistributor.profile.lastName}`
      : 'Unknown';
    const companyName = getCompanyName(selectedDistributor);

    // Build CSV
    const csvRows: string[] = [];

    // Header row
    csvRows.push([
      'Invoice Number',
      'Physician Name',
      'Supervisor',
      'Account Manager',
      'Paid Date',
      'Product Description',
      'Quantity',
      'Product Cost (Floor)',
      'AM Cost (Catalog)',
      'AM Price (Invoice)',
      'Unit Margin',
      'Total AM Payout'
    ].join(','));

    // Data rows
    for (const invoice of revenuePreview.invoiceDetails) {
      for (const item of invoice.items) {
        csvRows.push([
          `"${invoice.invoiceNumber}"`,
          `"${invoice.physicianName}"`,
          `"${(invoice.supervisorName || '').replace(/"/g, '""')}"`,
          `"${(invoice.accountManagerName || '').replace(/"/g, '""')}"`,
          `"${formatShortDate(invoice.paidAt)}"`,
          `"${item.description.replace(/"/g, '""')}"`,
          item.quantity.toString(),
          item.productCost.toFixed(2),
          item.amCost.toFixed(2),
          item.amPrice.toFixed(2),
          item.unitMargin.toFixed(2),
          item.amPayout.toFixed(2)
        ].join(','));
      }
    }

    // Add summary row
    csvRows.push('');
    csvRows.push(`"Payout Preview for ${recipientName}${companyName ? ` (${companyName})` : ''}"`);
    csvRows.push(`"Period: ${createForm.startDate} - ${createForm.endDate}"`);
    csvRows.push(`"Total Invoices: ${revenuePreview.invoiceCount}"`);
    csvRows.push(`"Total Revenue: $${revenuePreview.revenue.toFixed(2)}"`);
    if (revenuePreview.previouslyPaidRevenue > 0) {
      csvRows.push(`"Previously Paid Revenue: $${revenuePreview.previouslyPaidRevenue.toFixed(2)}"`);
      csvRows.push(`"Total Revenue in Range: $${revenuePreview.totalRevenueInRange.toFixed(2)}"`);
    }

    // Payout instructions block (the admin pasting this into wire/check tooling
    // shouldn't have to switch tabs to grab them).
    const payoutInstructions =
      selectedDistributor?.supervisorData?.paymentInstructions ||
      selectedDistributor?.distributorData?.paymentInstructions ||
      '';
    csvRows.push('');
    csvRows.push('"Payout Instructions:"');
    if (payoutInstructions) {
      csvRows.push(`"${payoutInstructions.replace(/"/g, '""')}"`);
    } else {
      csvRows.push('"(none on file)"');
    }

    const csvContent = csvRows.join('\n');
    const filename = `payout-preview-${recipientName.replace(/\s+/g, '-').toLowerCase()}-${createForm.startDate}-to-${createForm.endDate}.csv`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    notifySuccess('CSV Downloaded', 'The payout preview has been exported to CSV.');
  };

  const resetCreateForm = () => {
    setCreateForm({
      recipientId: '',
      startDate: '',
      endDate: '',
      amount: '',
      wireNumber: '',
      description: '',
      notesForRecipient: ''
    });
    setRevenuePreview(null);
    // Reset distributor search/selection state
    setDistributorSearch('');
    setShowDistributorDropdown(false);
    setDistributorPage(1);
    setSelectedRecipient(null);
  };

  // Paginate the recipient list for dropdown display. The list is already
  // filtered server-side (see loadDistributors), so no client-side name filter
  // here — that would wrongly hide server matches on fields the browser can't
  // see (phone, NPI, clinic name).
  const getFilteredDistributors = () => {
    const filtered = distributors;

    const total = filtered.length;
    const totalPages = Math.ceil(total / DISTRIBUTORS_PER_PAGE);
    const startIndex = (distributorPage - 1) * DISTRIBUTORS_PER_PAGE;
    const endIndex = Math.min(startIndex + DISTRIBUTORS_PER_PAGE, total);
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return { filtered, total, totalPages, startIndex, endIndex, paginatedItems };
  };

  // Get currently selected distributor. Prefer the record captured at selection
  // time so it survives the recipient list being replaced by a later search.
  const getSelectedDistributor = () => {
    if (selectedRecipient && selectedRecipient._id === createForm.recipientId) {
      return selectedRecipient;
    }
    return distributors.find(d => d._id === createForm.recipientId);
  };

  // Filter payouts based on search and status
  const filteredPayouts = payouts.filter(payout => {
    const matchesSearch = searchTerm === '' || 
      payout.recipientId?.profile?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payout.recipientId?.profile?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payout.recipientId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCompanyName(payout.recipientId)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payout.wireNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payout.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>;
      case 'sent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Send className="w-3 h-3 mr-1" />
          Sent
        </span>;
      case 'confirmed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Confirmed
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Cancelled
        </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Failed
        </span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {status}
        </span>;
    }
  };

  /**
   * Badge for a payout's rail state.
   *
   * PayQuicker's status vocabulary is stored verbatim, so anything we don't
   * explicitly recognize is shown as "unknown" rather than being dressed up as a
   * healthy state — an unrecognized status needs a human, not a green tick.
   */
  const getRailBadge = (rail?: Payout['rail']) => {
    if (!rail) return null;
    const status = (rail.providerStatus || '').toUpperCase();

    const chip = (cls: string, icon: React.ReactNode, label: string, title?: string) => (
      <span
        title={title || `PayQuicker: ${rail.providerStatus || 'unknown'}`}
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}
      >
        {icon}
        {label}
      </span>
    );

    if (['COMPLETE', 'COMPLETED'].includes(status)) {
      return chip('bg-green-100 text-green-800', <CheckCircle className="w-3 h-3 mr-1" />, 'Settled');
    }
    if (['FAILED', 'RETURNED', 'REVERSED', 'REFUNDED', 'CANCELLED', 'CANCELED', 'EXPIRED', 'VOIDED'].includes(status)) {
      return chip('bg-red-100 text-red-800', <XCircle className="w-3 h-3 mr-1" />, status === 'REVERSED' ? 'Reversed' : 'Failed');
    }
    if (status === 'UNKNOWN' || status === 'CLAIMED') {
      return chip(
        'bg-orange-100 text-orange-800',
        <HelpCircle className="w-3 h-3 mr-1" />,
        'Unresolved',
        'PayQuicker did not confirm the outcome. Reconciliation will resolve this — do not re-send.'
      );
    }
    if (status === 'REJECTED') {
      return chip('bg-yellow-100 text-yellow-800', <AlertCircle className="w-3 h-3 mr-1" />, 'Rejected');
    }
    if (status.startsWith('REVIEW')) {
      return chip('bg-purple-100 text-purple-800', <Clock className="w-3 h-3 mr-1" />, 'In review');
    }
    if (['ACCEPTED', 'PENDING', 'PROCESSING', 'SCHEDULED', 'UNSETTLED', 'QUOTED', 'PENDING_ACCEPTANCE', 'VERIFICATION_HOLD'].includes(status)) {
      return chip('bg-blue-100 text-blue-800', <Clock className="w-3 h-3 mr-1" />, 'In transit');
    }
    return chip('bg-gray-100 text-gray-800', <HelpCircle className="w-3 h-3 mr-1" />, rail.providerStatus || 'Unknown');
  };

  /**
   * Payouts whose rail outcome needs resolving. Derived from the loaded list
   * rather than a second request — these are exactly the states an admin must not
   * resolve by re-sending.
   */
  const railAttentionCount = payouts.filter(p => {
    const status = (p.rail?.providerStatus || '').toUpperCase();
    return status === 'UNKNOWN' || status === 'CLAIMED' || status.startsWith('REVIEW');
  }).length;

  /** Short, human label for a recipient's onboarding state. */
  const getOnboardingLabel = (status?: string) => {
    switch (status) {
      case 'approved': return 'PayQuicker ready';
      case 'invited': return 'Invitation sent';
      case 'in_progress': return 'Registering';
      case 'rejected': return 'PayQuicker declined';
      default: return 'Not on PayQuicker';
    }
  };

  // Calculate summary statistics
  const summaryStats = {
    total: payouts.length,
    pending: payouts.filter(p => p.status === 'pending').length,
    confirmed: payouts.filter(p => p.status === 'confirmed').length,
    totalAmount: payouts.reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0)
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Payouts</h1>
          <p className="text-gray-600 mt-2">Track and manage payments to supervisors and account managers</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Payouts</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.totalAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.pendingAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Confirmed</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.confirmed}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions and Filters */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search recipients, wire numbers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setBulkRows([]);
                    setBulkSelected(new Set());
                    setBulkErrors([]);
                    setShowBulkModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-900 rounded-lg hover:bg-amber-200 focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 transition-colors"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Bulk drafts
                </button>
                {railAttentionCount > 0 && (
                  <button
                    onClick={runRailReconciliation}
                    disabled={reconciling}
                    className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-900 rounded-lg hover:bg-orange-200 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    title="Re-read PayQuicker for payouts whose outcome is unresolved. Runs automatically every 15 minutes."
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
                    {reconciling
                      ? 'Reconciling…'
                      : `Reconcile ${railAttentionCount} payout${railAttentionCount === 1 ? '' : 's'}`}
                  </button>
                )}
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Payout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-2 text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Payouts Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading payouts...</p>
            </div>
          ) : filteredPayouts.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No payouts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {payouts.length === 0 ? 'Get started by creating your first payout.' : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Recipient</th>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Wire Number</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayouts.map((payout) => (
                      <tr key={payout._id}>
                        <td className="cell-primary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">
                              {payout.recipientId?.profile?.firstName} {payout.recipientId?.profile?.lastName || 'Unknown User'}
                            </span>
                            {getCompanyName(payout.recipientId) && (
                              <span className="cell-stack-label">
                                {getCompanyName(payout.recipientId)}
                              </span>
                            )}
                            <span className="cell-stack-label cell-truncate">{payout.recipientId?.email || 'No email'}</span>
                          </div>
                        </td>
                        <td className="cell-secondary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{formatShortDate(payout.startDate)}</span>
                            <span className="cell-stack-label">to {formatShortDate(payout.endDate)}</span>
                          </div>
                        </td>
                        <td className="cell-mono">
                          {formatCurrency(payout.amount)}
                        </td>
                        <td className="cell-secondary">
                          <div className="flex flex-col gap-1 items-start">
                            {getStatusBadge(payout.status)}
                            {getRailBadge(payout.rail)}
                          </div>
                        </td>
                        <td className="cell-mono">
                          {payout.wireNumber || '-'}
                        </td>
                        <td className="cell-muted">
                          {formatShortDate(payout.createdAt)}
                        </td>
                        <td className="cell-actions">
                          <div className="table-actions">
                            <button
                              onClick={() => {
                                setSelectedPayout(payout);
                                setShowDetailsModal(true);
                                loadPayoutInvoiceBreakdown(payout._id);
                              }}
                              className="table-action-btn table-action-btn-secondary"
                              title="View Details"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              <span className="hidden lg:inline">View</span>
                            </button>
                            <button
                              onClick={() => downloadPayoutCSV(payout._id)}
                              className="table-action-btn table-action-btn-success"
                              title="Download CSV"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              <span className="hidden lg:inline">CSV</span>
                            </button>
                            {payout.status === 'pending' && payout.recipientPayQuickerReady && (
                              <button
                                onClick={() => sendViaPayQuicker(payout._id)}
                                disabled={sendingRailPayoutId === payout._id}
                                className="table-action-btn table-action-btn-primary disabled:opacity-50"
                                title="Send via PayQuicker"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">
                                  {sendingRailPayoutId === payout._id ? 'Sending…' : 'PayQuicker'}
                                </span>
                              </button>
                            )}
                            {payout.status === 'pending' && !payout.recipientPayQuickerReady && (
                              <button
                                onClick={() => inviteToPayQuicker(payout.recipientId._id, payout.recipientName || 'Recipient')}
                                disabled={
                                  invitingRecipientId === payout.recipientId._id ||
                                  payout.recipientPayQuickerStatus === 'rejected'
                                }
                                className="table-action-btn table-action-btn-secondary disabled:opacity-50"
                                title={
                                  payout.recipientPayQuickerStatus === 'rejected'
                                    ? 'PayQuicker declined this recipient — pay by manual wire'
                                    : `${getOnboardingLabel(payout.recipientPayQuickerStatus)} — invite to PayQuicker`
                                }
                              >
                                <UserPlus className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">
                                  {invitingRecipientId === payout.recipientId._id ? 'Inviting…' : 'Invite'}
                                </span>
                              </button>
                            )}
                            {payout.status === 'pending' && (
                              <button
                                onClick={() => markPayoutAsSent(payout._id)}
                                className="table-action-btn table-action-btn-primary"
                                title="Mark as sent by manual wire (no payment rail)"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">Wire</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Payout Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Create New Payout</h2>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              {/* Recipient Selection - Searchable Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient (Supervisor or Account Manager)
                </label>
                
                {/* Selected Distributor Display */}
                {createForm.recipientId && (() => {
                  const selected = getSelectedDistributor();
                  return selected ? (
                    <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-900">
                          {selected.profile.firstName} {selected.profile.lastName}
                        </div>
                        {getCompanyName(selected) && (
                          <div className="text-sm text-blue-700">{getCompanyName(selected)}</div>
                        )}
                        <div className="text-xs text-blue-600">{selected.email}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setCreateForm(prev => ({ ...prev, recipientId: '' }));
                          setSelectedRecipient(null);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        Change
                      </button>
                    </div>
                  ) : null;
                })()}
                
                {/* Search Input */}
                {!createForm.recipientId && (
                  <div className="relative distributor-dropdown-container">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search recipients by name, company, or email..."
                      value={distributorSearch}
                      onChange={(e) => {
                        setDistributorSearch(e.target.value);
                        setDistributorPage(1); // Reset to page 1 on new search
                        setShowDistributorDropdown(true);
                      }}
                      onFocus={() => setShowDistributorDropdown(true)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    
                    {/* Dropdown with Pagination */}
                    {showDistributorDropdown && (() => {
                      const { total, totalPages, paginatedItems, startIndex, endIndex } = getFilteredDistributors();
                      
                      if (total === 0) {
                        return (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            <div className="px-4 py-3 text-gray-500 text-sm">
                              {distributorSearch.trim()
                                ? `No recipients found matching "${distributorSearch}"`
                                : 'No recipients available'}
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                          {/* Dropdown Items */}
                          <div className="max-h-[280px] overflow-y-auto">
                            {paginatedItems.map(distributor => (
                              <div
                                key={distributor._id}
                                onClick={() => {
                                  setCreateForm(prev => ({ ...prev, recipientId: distributor._id }));
                                  setSelectedRecipient(distributor);
                                  setShowDistributorDropdown(false);
                                  setDistributorSearch('');
                                }}
                                className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-sm">
                                  {distributor.profile.firstName} {distributor.profile.lastName}
                                </div>
                                {getCompanyName(distributor) && (
                                  <div className="text-sm text-gray-600">{getCompanyName(distributor)}</div>
                                )}
                                <div className="text-xs text-gray-500">{distributor.email}</div>
                              </div>
                            ))}
                          </div>

                          {moreClinicsTotal !== null && (
                            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                              Showing the first 100{moreClinicsTotal >= 0 ? ` of ${moreClinicsTotal}` : ''} matching clinics. Type to narrow the search.
                            </div>
                          )}
                          
                          {/* Pagination Controls */}
                          {totalPages > 1 && (
                            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                              <span className="text-gray-600">
                                {startIndex + 1}-{endIndex} of {total}
                              </span>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (distributorPage > 1) setDistributorPage(distributorPage - 1);
                                  }}
                                  disabled={distributorPage === 1}
                                  className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Prev
                                </button>
                                <span className="text-gray-600">
                                  Page {distributorPage} of {totalPages}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (distributorPage < totalPages) setDistributorPage(distributorPage + 1);
                                  }}
                                  disabled={distributorPage === totalPages}
                                  className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const date = new Date();
                        date.setDate(date.getDate() - 14);
                        setCreateForm(prev => ({ ...prev, startDate: date.toISOString().split('T')[0] }));
                      }}
                      className="px-3 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition-colors border border-blue-200"
                    >
                      2 Weeks Ago
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const date = new Date();
                        date.setMonth(date.getMonth() - 1);
                        setCreateForm(prev => ({ ...prev, startDate: date.toISOString().split('T')[0] }));
                      }}
                      className="px-3 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition-colors border border-blue-200"
                    >
                      1 Month Ago
                    </button>
                  </div>
                  <input
                    type="date"
                    value={createForm.startDate}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        setCreateForm(prev => ({ ...prev, endDate: today.toISOString().split('T')[0] }));
                      }}
                      className="px-3 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition-colors border border-blue-200"
                    >
                      Today
                    </button>
                  </div>
                  <input
                    type="date"
                    value={createForm.endDate}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Revenue Preview Button */}
              {createForm.recipientId && createForm.startDate && createForm.endDate && (
                <div>
                  <button
                    type="button"
                    onClick={calculateRevenuePreview}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg transition-colors"
                  >
                    Calculate Revenue for Period
                  </button>
                </div>
              )}

              {/* Revenue Preview Display */}
              {revenuePreview && (
                <div className="space-y-4">
                  {/* Overlapping Payouts Warning */}
                  {revenuePreview.hasOverlappingPayouts && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-900 mb-1">Overlapping Payouts Detected</h4>
                          <p className="text-sm text-amber-800 mb-2">
                            There are existing payouts that overlap with this date range. Revenue from those periods has already been paid.
                          </p>
                          <div className="space-y-1">
                            {revenuePreview.overlappingPayouts.map((p) => (
                              <div key={p.payoutId} className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                                {formatShortDate(p.startDate)} - {formatShortDate(p.endDate)}: {formatCurrency(p.amount)} ({p.status})
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Revenue Breakdown - New vs Previously Paid */}
                  {(revenuePreview.previouslyPaidRevenue > 0 || revenuePreview.hasOverlappingPayouts) && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Revenue Breakdown for Date Range</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center py-1 border-b border-gray-200">
                          <span className="text-gray-600">Total revenue in date range:</span>
                          <span className="font-medium">{formatCurrency(revenuePreview.totalRevenueInRange)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-gray-200">
                          <span className="text-gray-600">
                            Previously paid out ({revenuePreview.previouslyPaidOrderCount} orders, {revenuePreview.previouslyPaidInvoiceCount} invoices):
                          </span>
                          <span className="font-medium text-gray-500">- {formatCurrency(revenuePreview.previouslyPaidRevenue)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 pt-2">
                          <span className="text-green-700 font-medium">New revenue to pay out:</span>
                          <span className="font-bold text-green-700">{formatCurrency(revenuePreview.revenue)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Main Revenue Preview */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">
                      {revenuePreview.previouslyPaidRevenue > 0 ? 'New Revenue to Pay Out' : 'Revenue Preview'}
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-blue-600">Amount</p>
                        <p className="font-bold text-blue-900">{formatCurrency(revenuePreview.revenue)}</p>
                      </div>
                      <div>
                        <p className="text-blue-600">New Orders</p>
                        <p className="font-bold text-blue-900">{revenuePreview.orderCount}</p>
                      </div>
                      <div>
                        <p className="text-blue-600">New Invoices</p>
                        <p className="font-bold text-blue-900">{revenuePreview.invoiceCount}</p>
                      </div>
                    </div>
                    {revenuePreview.revenue === 0 && revenuePreview.previouslyPaidRevenue > 0 && (
                      <div className="mt-3 text-sm text-amber-700 bg-amber-50 rounded p-2">
                        ⚠️ All revenue in this date range has already been paid out.
                      </div>
                    )}
                  </div>
                  
                  {/* Invoice Breakdown */}
                  {revenuePreview.invoiceDetails && revenuePreview.invoiceDetails.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <InvoiceBreakdown
                        invoices={revenuePreview.invoiceDetails}
                        title="New Invoices to Include in This Payout"
                        showRevenue={true}
                      />
                    </div>
                  )}

                  {/* Download Preview CSV Button */}
                  {revenuePreview.invoiceDetails && revenuePreview.invoiceDetails.length > 0 && (
                    <button
                      type="button"
                      onClick={downloadPreviewCSV}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Preview as CSV
                    </button>
                  )}
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payout Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Wire Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wire Transfer Number (Optional)
                </label>
                <input
                  type="text"
                  value={createForm.wireNumber}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, wireNumber: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="WIRE123456"
                />
              </div>

              {/* Internal Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Description (Admin Only)
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Internal notes about this payout (not visible to recipient)"
                />
              </div>

              {/* Notes for Recipient */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes for Recipient
                </label>
                <textarea
                  value={createForm.notesForRecipient}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, notesForRecipient: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Message that will be visible to the recipient"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={createPayout}
                disabled={submitting || !createForm.recipientId || !createForm.startDate || !createForm.endDate || !createForm.amount}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Creating...' : 'Create Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Drafts Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Bulk-draft payouts for a period</h2>
              <p className="text-sm text-gray-500 mt-1">
                Scans every active AM, supervisor, and physician for owed revenue and creates one pending draft per recipient.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="flex gap-3 items-end">
                <label className="block">
                  <span className="text-xs text-gray-500">Start date</span>
                  <input
                    type="date"
                    value={bulkRange.startDate}
                    onChange={e => setBulkRange(r => ({ ...r, startDate: e.target.value }))}
                    className="border rounded px-2 py-1.5 w-44"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">End date</span>
                  <input
                    type="date"
                    value={bulkRange.endDate}
                    onChange={e => setBulkRange(r => ({ ...r, endDate: e.target.value }))}
                    className="border rounded px-2 py-1.5 w-44"
                  />
                </label>
                <button
                  type="button"
                  disabled={bulkLoading || !bulkRange.startDate || !bulkRange.endDate}
                  onClick={async () => {
                    setBulkLoading(true);
                    setBulkErrors([]);
                    const res = await apiClient.getBulkPayoutPreview(bulkRange);
                    setBulkLoading(false);
                    if (res.success && res.data) {
                      setBulkRows(res.data.rows || []);
                      setBulkSelected(new Set((res.data.rows || []).map((r: any) => r.recipientId)));
                    } else {
                      notifyError('Preview failed', res.error || 'Could not load bulk preview');
                    }
                  }}
                  className="bg-blue-600 text-white rounded px-4 py-1.5 hover:bg-blue-700 disabled:opacity-50"
                >
                  {bulkLoading ? 'Loading…' : 'Preview'}
                </button>
              </div>

              {bulkRows.length > 0 && (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">
                            <input
                              type="checkbox"
                              checked={bulkSelected.size === bulkRows.length && bulkRows.length > 0}
                              onChange={e => {
                                if (e.target.checked) setBulkSelected(new Set(bulkRows.map(r => r.recipientId)));
                                else setBulkSelected(new Set());
                              }}
                            />
                          </th>
                          <th className="px-3 py-2 text-left">Recipient</th>
                          <th className="px-3 py-2 text-left">Role</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-right">Orders</th>
                          <th className="px-3 py-2 text-right">Invoices</th>
                          <th className="px-3 py-2 text-right">Membership</th>
                          <th className="px-3 py-2 text-left">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkRows.map(row => {
                          const checked = bulkSelected.has(row.recipientId);
                          const error = bulkErrors.find(e => e.recipientId === row.recipientId);
                          return (
                            <tr key={row.recipientId} className={`border-t ${error ? 'bg-red-50' : ''}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={e => {
                                    setBulkSelected(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(row.recipientId);
                                      else next.delete(row.recipientId);
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium">{row.recipientName}</div>
                                <div className="text-xs text-gray-500">{row.recipientCompany || row.recipientEmail}</div>
                              </td>
                              <td className="px-3 py-2 capitalize">{row.recipientRole === 'distributor' ? 'AM' : row.recipientRole}</td>
                              <td className="px-3 py-2 text-right font-mono">
                                {formatCurrency(row.amount)}
                                {!!row.overrideEarnings && (
                                  <div className="text-[10px] text-indigo-600 font-sans">incl. {formatCurrency(row.overrideEarnings)} override</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">{row.orderCount}</td>
                              <td className="px-3 py-2 text-right">{row.invoiceCount}</td>
                              <td className="px-3 py-2 text-right">{row.membershipEarnings ? formatCurrency(row.membershipEarnings) : '—'}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">
                                {row.hasOverlappingPayout && <span className="text-amber-700">⚠ overlap</span>}
                                {error && <div className="text-red-700">{error.error}</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div>
                      {bulkSelected.size} of {bulkRows.length} selected • Total: <span className="font-mono font-medium">
                        {formatCurrency(bulkRows.filter(r => bulkSelected.has(r.recipientId)).reduce((s, r) => s + r.amount, 0))}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {bulkRows.length === 0 && !bulkLoading && (
                <div className="text-sm text-gray-500 py-8 text-center border border-dashed rounded">
                  No preview yet — pick a date range and click Preview.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={bulkSubmitting}
              >
                Close
              </button>
              <button
                type="button"
                disabled={bulkRows.length === 0}
                onClick={() => {
                  // Build a combined CSV across every recipient currently in the
                  // preview (or only the selected ones if any are checked) with
                  // amount + payout instructions in a single sheet — saves the
                  // admin from bouncing between screens to grab wires/checks.
                  const subset = bulkSelected.size > 0
                    ? bulkRows.filter(r => bulkSelected.has(r.recipientId))
                    : bulkRows;
                  const escape = (v: any) => {
                    const s = v == null ? '' : String(v);
                    return `"${s.replace(/"/g, '""')}"`;
                  };
                  const lines: string[] = [];
                  lines.push([
                    'Role', 'Recipient', 'Company', 'Email',
                    'Supervisor', 'Account Manager',
                    'Amount Owed', 'Orders', 'Invoices', 'Membership', 'Override',
                    'Payment Instructions'
                  ].join(','));
                  for (const r of subset) {
                    lines.push([
                      escape(r.recipientRole === 'distributor' ? 'AM' : r.recipientRole),
                      escape(r.recipientName),
                      escape(r.recipientCompany || ''),
                      escape(r.recipientEmail || ''),
                      escape(r.supervisorName || ''),
                      escape(r.accountManagerName || ''),
                      r.amount.toFixed(2),
                      r.orderCount,
                      r.invoiceCount,
                      (r.membershipEarnings || 0).toFixed(2),
                      (r.overrideEarnings || 0).toFixed(2),
                      escape(r.paymentInstructions || '')
                    ].join(','));
                  }
                  lines.push('');
                  lines.push(escape(`Period: ${bulkRange.startDate} → ${bulkRange.endDate}`));
                  lines.push(escape(`Recipients: ${subset.length}`));
                  lines.push(escape(`Total owed: $${subset.reduce((s, r) => s + r.amount, 0).toFixed(2)}`));

                  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `payouts-combined-${bulkRange.startDate}-to-${bulkRange.endDate}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  notifySuccess('Sheet downloaded', `${subset.length} recipient${subset.length === 1 ? '' : 's'} exported`);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Download Sheet
              </button>
              <button
                type="button"
                disabled={bulkSubmitting || bulkSelected.size === 0}
                onClick={async () => {
                  setBulkSubmitting(true);
                  setBulkErrors([]);
                  const res = await apiClient.bulkCreatePayouts({
                    startDate: bulkRange.startDate,
                    endDate: bulkRange.endDate,
                    recipientIds: Array.from(bulkSelected)
                  });
                  setBulkSubmitting(false);
                  if (res.success && res.data) {
                    notifySuccess('Drafts created', `${res.data.created.length} payouts created${res.data.errors.length ? `, ${res.data.errors.length} errors` : ''}`);
                    setBulkErrors(res.data.errors || []);
                    // Remove successfully created from preview
                    const createdIds = new Set(res.data.created.map((c: any) => c.recipientId));
                    setBulkRows(prev => prev.filter(r => !createdIds.has(r.recipientId)));
                    setBulkSelected(new Set());
                    // Reload main payout list
                    loadPayouts();
                    if (res.data.errors.length === 0) {
                      setShowBulkModal(false);
                    }
                  } else {
                    notifyError('Bulk create failed', res.error || 'Unknown error');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {bulkSubmitting ? 'Creating…' : `Create ${bulkSelected.size} draft${bulkSelected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout Details Modal */}
      {showDetailsModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Payout Details</h2>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* Status and Basic Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
                  {getStatusBadge(selectedPayout.status)}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Amount</h3>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedPayout.amount)}</p>
                </div>
              </div>

              {/* Recipient Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Recipient</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900">
                    {selectedPayout.recipientId?.profile?.firstName} {selectedPayout.recipientId?.profile?.lastName || 'Unknown User'}
                  </p>
                  {getCompanyName(selectedPayout.recipientId) && (
                    <p className="text-gray-600">{getCompanyName(selectedPayout.recipientId)}</p>
                  )}
                  <p className="text-sm text-gray-500">{selectedPayout.recipientId?.email || 'No email'}</p>
                </div>
              </div>

              {/* Period and Wire Information */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Period</h3>
                  <p className="text-gray-900">
                    {formatShortDate(selectedPayout.startDate)} - {formatShortDate(selectedPayout.endDate)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Wire Number</h3>
                  <p className="text-gray-900">{selectedPayout.wireNumber || 'Not provided'}</p>
                </div>
              </div>

              {/* Notes */}
              {selectedPayout.description && (
                <div>
                  <div className='flex flex-row'>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Internal Description</h3>
                    <h3 className='text-[10px] font-medium text-green-700 relative top-1 mb-2'>(Admin Only)</h3>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-gray-900">{selectedPayout.description}</p>
                  </div>
                </div>
              )}

              {selectedPayout.notesForRecipient && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Notes for Recipient</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-gray-900">{selectedPayout.notesForRecipient}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Created</h3>
                  <p className="text-gray-900">{formatDateTime(selectedPayout.createdAt)}</p>
                  <p className="text-sm text-gray-500">
                    by {selectedPayout.createdById?.profile?.firstName} {selectedPayout.createdById?.profile?.lastName || 'Unknown User'}
                  </p>
                </div>
                {selectedPayout.sentAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Sent</h3>
                    <p className="text-gray-900">{formatDateTime(selectedPayout.sentAt)}</p>
                  </div>
                )}
                {selectedPayout.confirmedAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Confirmed</h3>
                    <p className="text-gray-900">{formatDateTime(selectedPayout.confirmedAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Earnings / Invoice Breakdown Section */}
            <div className="px-6 py-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {payoutInvoiceBreakdown?.physicianBreakdown ? 'Earnings Breakdown' : 'Invoice Breakdown'}
              </h3>
              {loadingInvoiceBreakdown ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-500 mt-2">Loading details...</p>
                </div>
              ) : payoutInvoiceBreakdown?.physicianBreakdown ? (
                <PhysicianPayoutBreakdown breakdown={payoutInvoiceBreakdown.physicianBreakdown} />
              ) : payoutInvoiceBreakdown?.invoiceDetails ? (
                <InvoiceBreakdown
                  invoices={payoutInvoiceBreakdown.invoiceDetails}
                  title={`Invoices for ${formatShortDate(selectedPayout.startDate)} - ${formatShortDate(selectedPayout.endDate)}`}
                  showRevenue={true}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No breakdown available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <div className="flex gap-2">
                {selectedPayout.status === 'pending' && selectedPayout.recipientPayQuickerReady && (
                  <button
                    onClick={() => {
                      sendViaPayQuicker(selectedPayout._id);
                      setShowDetailsModal(false);
                    }}
                    disabled={sendingRailPayoutId === selectedPayout._id}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send via PayQuicker
                  </button>
                )}
                {selectedPayout.status === 'pending' && (
                  <button
                    onClick={() => {
                      markPayoutAsSent(selectedPayout._id);
                      setShowDetailsModal(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    title="Record a manual wire — no payment rail involved"
                  >
                    <Send className="w-4 h-4" />
                    Mark as Wired
                  </button>
                )}
                <button
                  onClick={() => downloadPayoutCSV(selectedPayout._id)}
                  disabled={downloadingCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {downloadingCSV ? 'Downloading...' : 'Download CSV'}
                </button>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
